import {
  useEffect, useRef, useState, useMemo,
  useCallback,
} from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import {
  Alert,
  Snackbar,
  Typography,
  useTheme,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  ResetFilteredContentList,
  selectFilteredContentList,
  selectFilteredContentPageNumber,
  selectHasMoreFilteredContent,
  UpdateHasMoreFilteredContent,
  UpdateFilteredContentPageNumber,
  selectGetFilteredContentListState,
} from '../../../store/slices/contentSlice';
import { getFilteredContent } from '../../../store/thunks/contentThunk';
import { useScrollRestoration } from '../../../common/hooks/useScrollRestoration';
import { useIsMobile } from '../../../common/hooks/useResponsive';
import { useTrackCategoryContentVisibility } from './useTrackCategoryContentVisibility';
import { Content, FeedInteractionAction } from '../../../common/models/content/Content';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import ContentViewMoreContainer from './ContentViewMoreContainer';
import { Logger } from '../../../config/logging/logger';
import ContentHorizontalCardSkeleton from './ContentHorizontalCardSkeleton';
import { GetContentRequestV4 } from '../../../common/models/content/GetContentRequest';

interface FilteredContentCategoryProps {
  categoryId: string;
  keyProp: string;
  computedOptions: string;
  selectedFilter: string;
  setSomeContentLoadedInitially: (value: boolean) => void
}

export function FilteredContentCategory({
  categoryId,
  keyProp,
  computedOptions,
  selectedFilter,
  setSomeContentLoadedInitially,
}: FilteredContentCategoryProps) {
  const filteredStoreContentList = useAppSelector(selectFilteredContentList);
  const filteredContentList = useMemo(() => filteredStoreContentList.map((content, indx) => (
    {
      ...content,
      contentFeedSection: { index: -1, name: `filtered: ${selectedFilter}` },
      contentFeedSectionPosition: indx + 1,
    } as Content
  )), [filteredStoreContentList, selectedFilter]);
  const contentFetchInitiated = useRef(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const { trackFeedInteraction } = useCustomEventTracking();
  const storeHasMoreFilteredContent = useAppSelector(selectHasMoreFilteredContent);
  const [hasMoreFilteredContent, setHasMoreFilteredContent] = useState(storeHasMoreFilteredContent);
  const page = useAppSelector(selectFilteredContentPageNumber);
  const getFilteredContentListState = useAppSelector(selectGetFilteredContentListState);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // Extract tag constants to match ContentHome
  // Ensures localization changes don't break filter logic
  const TOOLS_FILTER_TAG = t('home.contentFilters.tools.tag');
  const NEW_FILTER_TAG = t('home.contentFilters.new.tag');

  useTrackCategoryContentVisibility({
    categoryContentList: filteredContentList,
  });
  const theme = useTheme();
  const isMobile = useIsMobile();
  const pageSize = isMobile ? 5 : 15;
  const logger = Logger.create('FilteredContentCategory');
  const { restoreScroll } = useScrollRestoration();
  useEffect(() => {
    setHasMoreFilteredContent(storeHasMoreFilteredContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeHasMoreFilteredContent]);

  const isLoading = useMemo(
    () => (getFilteredContentListState === 'pending' && !initialFetchDone),
    [getFilteredContentListState, initialFetchDone],
  );

  const fetchFilteredContent = useCallback((currentPage: number) => {
    const filterRequest: GetContentRequestV4 = {
      pageSize,
      page: currentPage,
    };
    if (selectedFilter === NEW_FILTER_TAG) {
      filterRequest.sortBy = 'source_published_date';
      filterRequest.sortOrder = 'desc';
      filterRequest.businessTrades = computedOptions;
    } else if (selectedFilter === TOOLS_FILTER_TAG) {
      filterRequest.membershipTiers = computedOptions;
    } else {
      filterRequest.contentTopics = computedOptions;
    }
    return dispatch(getFilteredContent(filterRequest));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, computedOptions, pageSize, NEW_FILTER_TAG, TOOLS_FILTER_TAG]);

  useEffect(
    () => {
      if (restoreScroll()) {
        return;
      }
      if (!contentFetchInitiated.current) {
        contentFetchInitiated.current = true;
        dispatch(ResetFilteredContentList([])); // initial filtered content list state
        fetchFilteredContent(1)
          .then(unwrapResult)
          .then((res) => {
            setOpenErrorSnackbar(false);
            setSomeContentLoadedInitially(true);
            setInitialFetchDone(true);
            // Move to page to 2 so infinite scroll can begin from there
            dispatch(UpdateFilteredContentPageNumber(2));
            if (res.length === pageSize) {
              dispatch(UpdateHasMoreFilteredContent(true));
            } else {
              dispatch(UpdateHasMoreFilteredContent(false));
            }
          }).catch((err) => {
            setOpenErrorSnackbar(true);
            logger.error(Logger.format(err));
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchFilteredContent, pageSize],
  );

  const fetchMoreFilteredContent = () => {
    if (page > 1) {
      trackFeedInteraction(
        'Load',
        'MoreContent',
        {
          action: FeedInteractionAction.ReachEnd,
        },
      );
    }
    fetchFilteredContent(page)
      .then(unwrapResult)
      .then((res) => {
        setOpenErrorSnackbar(false);
        dispatch(UpdateHasMoreFilteredContent(res.length === pageSize));
        if (hasMoreFilteredContent) {
          dispatch(UpdateFilteredContentPageNumber(page + 1));
        }
      })
      .catch((e) => {
        setOpenErrorSnackbar(true);
        logger.error(Logger.format(e));
      });
  };

  const handleCloseErrorSnackBar = () => {
    setOpenErrorSnackbar(false);
  };

  return (
    <>
      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseErrorSnackBar}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>
      <Typography variant="h12"
        sx={{
          ml: theme.spacing(4),
          [theme.breakpoints.down('md')]: {
            ml: 0,
          },
        }}>
        {selectedFilter === TOOLS_FILTER_TAG ? t('home.contentFilters.tools.title') : selectedFilter}
      </Typography>
      {isLoading ?
        <ContentHorizontalCardSkeleton sx={{ paddingLeft: 0 }} /> :
        <Box sx={{
          [theme.breakpoints.down('md')]: {
            pt: theme.spacing(2),
          },
        }}>
          <ContentViewMoreContainer
            key={`${keyProp}-content`}
            categoryId={categoryId}
            storeContentList={filteredContentList}
            onNextPage={fetchMoreFilteredContent}
            hasMoreContent={hasMoreFilteredContent}
            showSearchBar={false}
            showBackBtn={false}
            showTradeName={false}
          />
        </Box>}
    </>
  );
}
